import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { SPICE_LEVELS, type SpiceLevel } from '@/types';

interface SpiceLevelSelectorProps {
  value: SpiceLevel;
  onChange: (spiceLevel: SpiceLevel) => void;
  className?: string;
  onFieldFocus?: () => void;
  showAsUnselected?: boolean;
}

/**
 * SpiceLevelSelector - Indian spice level categorization system
 * Shows spice levels with visual indicators and custom descriptions
 */
export const SpiceLevelSelector: React.FC<SpiceLevelSelectorProps> = ({
  value,
  onChange,
  className,
  onFieldFocus,
  showAsUnselected = false,
}) => {
  const handleSpiceLevelSelect = (level: SpiceLevel['level']) => {
    // If the same level is clicked again and it's currently selected, unselect it by setting to 'none'
    if (!showAsUnselected && value.level === level) {
      const unselectedSpiceLevel: SpiceLevel = { ...value, level: 'none' };
      onChange(unselectedSpiceLevel);
    } else {
      const updatedSpiceLevel: SpiceLevel = { ...value, level };
      onChange(updatedSpiceLevel);
    }
    onFieldFocus?.();
  };

  const handlePreparationNotesChange = (notes: string) => {
    const updatedSpiceLevel = { ...value, preparationNotes: notes };
    onChange(updatedSpiceLevel);
    onFieldFocus?.();
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        {/* Spice level grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(SPICE_LEVELS).map(([key, spiceData]) => (
            <motion.button
              key={key}
              type="button"
              onClick={() => handleSpiceLevelSelect(key as SpiceLevel['level'])}
              onFocus={onFieldFocus}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                "relative p-3 rounded-lg border-2 transition-all duration-200",
                "flex flex-col items-center text-center space-y-2",
                "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2",
                (!showAsUnselected && value.level === key)
                  ? "border-current bg-current/10 text-gray-900 focus:ring-current/20"
                  : "border-gray-200 bg-white hover:border-gray-300 focus:ring-primary/20"
              )}
              style={{
                ...(!showAsUnselected && value.level === key && {
                  borderColor: spiceData.color,
                  backgroundColor: `${spiceData.color}10`,
                }),
              }}
            >
              {/* Spice label */}
              <span className="text-sm font-medium">
                {spiceData.label}
              </span>
              
              {/* Selection indicator */}
              {!showAsUnselected && value.level === key && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: spiceData.color }}
                >
                  ✓
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Selected spice level description */}
        {value.level && value.level !== 'none' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-gray-50 rounded-lg p-3"
          >
            <p className="text-sm text-gray-700">
              <strong>{SPICE_LEVELS[value.level].label}:</strong> Selected spice level for this dish.
            </p>
          </motion.div>
        )}

        {/* Preparation notes */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-600">
            Preparation Notes (Optional)
          </label>
          <input
            type="text"
            value={value.preparationNotes || ''}
            onChange={(e) => handlePreparationNotesChange(e.target.value)}
            placeholder="e.g., 'Can be made less spicy on request'"
            onFocus={onFieldFocus}
            className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-base transition-all duration-200"
          />
          <p className="text-xs text-gray-500">
            Add notes about spice customization or preparation methods
          </p>
        </div>

        {/* Spice level preview */}
        {value.level && value.level !== 'none' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center space-x-2 p-2 bg-orange-50 rounded-lg border border-orange-200"
          >
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800">
                {SPICE_LEVELS[value.level].label}
              </p>
              {value.preparationNotes && (
                <p className="text-xs text-orange-600 mt-1">
                  Note: {value.preparationNotes}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};